<template>
  <div class="ui-page credit-analysis-details">
    <ui-page-header
      title="Análise completa do crédito"
      :back="{ path: '/central-analise-creditos', query: { client_id: clientId } }"
      back-text="Central de Análise"
    >
      <v-btn v-if="$allowed('save')" color="primary" depressed :loading="saving" :disabled="!credit" @click="saveDraft">
        <v-icon left small>mdi-content-save-outline</v-icon>
        Salvar rascunho
      </v-btn>
    </ui-page-header>

    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <template v-if="credit">
      <ui-section title="1. Identificação do credor e do crédito" class="identification-section">
        <div class="identity-header">
          <div>
            <h3>{{ creditorName(credit.creditor_name) }}</h3>
            <p>{{ formatRegistration(credit.registration) }}</p>
          </div>
          <ui-badge :text="statusLabel(credit.status)" :color="statusColor(credit.status)" />
        </div>
        <dl class="metadata-grid">
          <div><dt>Empresa devedora</dt><dd>{{ credit.company_name }}</dd></div>
          <div><dt>Classe</dt><dd>{{ credit.creditor_type_label }}</dd></div>
          <div><dt>Moeda</dt><dd>{{ credit.currency_code }}</dd></div>
          <div><dt>Referência</dt><dd>{{ credit.reference || "Não informada" }}</dd></div>
          <div><dt>Responsável</dt><dd>{{ credit.responsible_name || "Sem responsável" }}</dd></div>
          <div><dt>Prazo</dt><dd>{{ deadlineLabel }}</dd></div>
          <div><dt>Sem movimentação</dt><dd>{{ credit.days_without_activity }} dia(s)</dd></div>
          <div><dt>Agrupamento</dt><dd>{{ credit.grouping_status === "suggested" ? "Não confirmado" : "Origem única" }}</dd></div>
        </dl>
      </ui-section>

      <ui-section title="2. Comparação entre as origens">
        <div class="comparison-table-wrapper">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>Campo</th>
                <th>Credor</th>
                <th>Recuperanda</th>
                <th>AJ</th>
                <th>Edital 52</th>
                <th>Edital 7</th>
                <th>Consolidado</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Valor</th>
                <td :class="{ divergent: valueDiverges }">{{ formatAmount(credit.values.creditor) }}</td>
                <td :class="{ divergent: valueDiverges }">{{ formatAmount(credit.values.debtor) }}</td>
                <td :class="{ divergent: valueDiverges }">{{ formatAmount(credit.values.aj) }}</td>
                <td>{{ formatAmount(qgcStageAmount(1)) }}</td>
                <td>{{ formatAmount(qgcStageAmount(2)) }}</td>
                <td>{{ formatAmount(qgcStageAmount(3)) }}</td>
              </tr>
              <tr><th>Classe</th><td colspan="6">{{ credit.creditor_type_label }}</td></tr>
              <tr><th>Moeda</th><td colspan="6">{{ credit.currency_code }}</td></tr>
              <tr><th>Devedora</th><td colspan="6">{{ credit.company_name }}</td></tr>
              <tr><th>Referência</th><td colspan="6">{{ credit.reference || "Não informada" }}</td></tr>
            </tbody>
          </table>
        </div>
      </ui-section>

      <ui-section title="3. Envios e análises relacionados" :description="`${credit.sources.length} envios`">
        <v-simple-table>
          <thead>
            <tr>
              <th>Origem</th><th>Nº</th><th>Tipo</th><th>Valor</th><th>Status</th><th>Atualizado em</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="source in credit.sources" :key="`${source.kind}-${source.reference_id}-${source.origin}`">
              <td><ui-badge :text="sourceLabel(source.origin)" :color="sourceColor(source.origin)" /></td>
              <td>#{{ source.reference_id }}</td>
              <td>{{ source.type }}</td>
              <td>{{ formatAmount(source.amount) }}</td>
              <td>{{ source.status }}</td>
              <td>{{ source.updated_at | defaultDate("DD/MM/YYYY HH:mm") }}</td>
              <td class="text-no-wrap">
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-btn icon small v-on="on" @click="openSource(source)"><v-icon small>mdi-arrow-right-circle-outline</v-icon></v-btn>
                  </template>
                  <span>Abrir nesta aba</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-btn icon small v-on="on" @click="openSourceNewTab(source)"><v-icon small>mdi-open-in-new</v-icon></v-btn>
                  </template>
                  <span>Abrir em nova aba</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <!-- Negocios do pedido de habilitacao/divergencia, para analisar sem sair da Central -->
        <div v-if="eventSources.length" class="mt-6">
          <div class="d-flex align-center justify-space-between flex-wrap mb-2">
            <h3 class="business-title">Negócios da habilitação/divergência</h3>
            <div>
              <v-btn small outlined color="primary" class="mr-2" @click="openSourceNewTab(eventSources[0])">
                <v-icon left small>mdi-open-in-new</v-icon> Abrir pedido em nova aba
              </v-btn>
              <v-btn small color="primary" depressed @click="scrollToDecision">
                <v-icon left small>mdi-clipboard-check-outline</v-icon> Analisar aqui
              </v-btn>
            </div>
          </div>

          <v-card v-for="source in eventSources" :key="`business-${source.item_id}`" outlined class="business-card mb-3">
            <v-card-text>
              <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                <div>
                  <strong>{{ source.type }} #{{ source.reference_id }}</strong>
                  <span class="ui-muted ml-2">negócio #{{ source.item_id }}</span>
                </div>
                <ui-badge :text="itemStatusLabel(source.item_status)" :color="itemStatusColor(source.item_status)" />
              </div>
              <v-row dense>
                <v-col cols="12" md="4"><small class="ui-muted">Origem do negócio</small><div>{{ source.business_origin || "—" }}</div></v-col>
                <v-col cols="12" md="4"><small class="ui-muted">Empresa devedora</small><div>{{ source.company_name || "—" }}</div></v-col>
                <v-col cols="12" md="4"><small class="ui-muted">Classe informada</small><div>{{ source.creditor_type_label || "—" }}</div></v-col>
                <v-col cols="12" md="4"><small class="ui-muted">Valor pleiteado</small><div>{{ formatAmount(source.amount) }}</div></v-col>
                <v-col cols="12" md="4"><small class="ui-muted">Enviado em</small><div>{{ source.created_at | defaultDate("DD/MM/YYYY HH:mm") }}</div></v-col>
                <v-col cols="12" md="4" v-if="source.partial_amount"><small class="ui-muted">Valor reconhecido parcial</small><div>{{ formatAmount(source.partial_amount) }}</div></v-col>
                <v-col cols="12" v-if="source.observation"><small class="ui-muted">Observação do credor</small><div class="business-text">{{ source.observation }}</div></v-col>
                <v-col cols="12" v-if="source.item_reason"><small class="ui-muted">Motivo já registrado</small><div class="business-text" v-html="source.item_reason"></div></v-col>
              </v-row>
              <div class="mt-2">
                <small class="ui-muted">Documentos do negócio</small>
                <div v-if="itemDocuments(source).length" class="d-flex flex-wrap" style="gap: 6px;">
                  <v-chip v-for="document in itemDocuments(source)" :key="document.document_id" small outlined :href="document.file" target="_blank">
                    <v-icon left small>mdi-file-document-outline</v-icon>{{ document.name }}
                  </v-chip>
                </div>
                <div v-else class="ui-muted">Nenhum documento anexado.</div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </ui-section>

      <ui-section title="4. Documentos relacionados" :description="`${credit.documents.length} documentos`">
        <div v-if="credit.documents.length" class="document-grid">
          <article v-for="document in credit.documents" :key="document.document_id">
            <v-icon color="primary">mdi-file-document-outline</v-icon>
            <div>
              <strong>{{ document.name }}</strong>
              <small>
                {{ sourceLabel(document.origin) }} · envio #{{ document.source_reference_id }} ·
                {{ document.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
              </small>
            </div>
            <v-btn icon small :href="document.file" target="_blank">
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
          </article>
        </div>
        <v-alert v-else text type="info" class="mb-0">
          Nenhum documento relacionado a este crédito.
        </v-alert>
      </ui-section>

      <ui-section title="Documentos solicitados" :description="`${(credit.document_requests || []).length} solicitações`">
        <template #actions>
          <v-btn small outlined color="primary" @click="openDocumentRequest('creditor')">
            <v-icon left small>mdi-file-account-outline</v-icon> Ao credor
          </v-btn>
          <v-btn small color="primary" depressed @click="openDocumentRequest('client')">
            <v-icon left small>mdi-file-send-outline</v-icon> À devedora
          </v-btn>
        </template>
        <div v-if="credit.document_requests && credit.document_requests.length">
          <v-card v-for="request in credit.document_requests" :key="`document-request-${request.id}`" outlined class="mb-3">
            <v-card-text>
              <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                <div>
                  <ui-badge
                    class="mr-2"
                    :text="request.target === 'creditor' ? 'Ao credor' : 'À devedora'"
                    :color="request.target === 'creditor' ? 'primary' : 'secondary'"
                  />
                  <strong>Solicitação #{{ request.id }}</strong>
                  <span class="ui-muted ml-2">enviada em {{ request.created_at | defaultDate("DD/MM/YYYY") }} · prazo {{ request.deadline | defaultDate("DD/MM/YYYY") }}</span>
                </div>
                <ui-badge :text="debtorRequestLabel(request)" :color="debtorRequestColor(request)" />
              </div>
              <div v-if="request.message" class="business-text mb-2">{{ request.message }}</div>
              <div v-if="request.justification" class="business-text mb-2"><strong>Justificativa da devedora:</strong> <span v-html="request.justification"></span></div>
              <v-simple-table dense>
                <thead><tr><th>Documento</th><th>Situação</th><th>Enviado em</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="document in request.documents" :key="`debtor-doc-${document.id}`">
                    <td>{{ document.name }}</td>
                    <td><ui-badge :text="document.tag" :color="document.file_url ? (document.uploaded_after_deadline ? 'error' : 'success') : 'warning'" /></td>
                    <td>{{ document.uploaded_at ? $options.filters.defaultDate(document.uploaded_at, "DD/MM/YYYY HH:mm") : "—" }}</td>
                    <td>
                      <div v-if="document.files && document.files.length" class="d-flex flex-column">
                        <a v-for="file in document.files" :key="`file-${document.id}-${file.id}`" :href="file.url" target="_blank" class="text-no-wrap">
                          <v-icon small>mdi-download</v-icon> {{ file.name }}
                        </a>
                      </div>
                      <v-btn v-else-if="document.file_url" icon small :href="document.file_url" target="_blank"><v-icon small>mdi-download</v-icon></v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </div>
        <v-alert v-else text type="info" class="mb-0">
          Nenhuma solicitação de documentos sobre este credor.
        </v-alert>
      </ui-section>

      <ui-section title="5. Histórico e auditoria" :description="`${credit.history.length} eventos`">
        <div class="history-timeline">
          <article v-for="(entry, index) in credit.history" :key="`${entry.kind}-${entry.reference_id}-${entry.item_id}-${index}`">
            <span :class="`timeline-dot ${entry.origin}`"></span>
            <div>
              <strong>{{ entry.description }}</strong>
              <small>
                {{ sourceLabel(entry.origin) }} · #{{ entry.reference_id }} ·
                {{ entry.occurred_at | defaultDate("DD/MM/YYYY HH:mm") }}
              </small>
            </div>
          </article>
        </div>
        <v-alert text type="info" class="audit-note">
          Salvamentos, bloqueios por duplicidade e conclusão da análise também são registrados na auditoria administrativa do caso.
        </v-alert>
      </ui-section>

      <!-- decision-anchor: alvo do scrollToDecision -->
      <ui-section title="6. Análise final" class="decision-section decision-anchor">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-select
              v-model="decision.outcome"
              :items="outcomeOptions"
              label="Resultado da análise"
              outlined
              dense
              @change="onOutcomeChange"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="decision.recognized_amount"
              type="number"
              label="Valor reconhecido"
              outlined
              dense
              :disabled="decision.outcome === 'rejected'"
              :hint="decision.outcome === 'partial' ? 'Obrigatório para aprovação parcial' : ''"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="decision.creditor_type" :items="creditorTypes" item-text="name" item-value="id" label="Classe reconhecida" outlined dense />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="decision.currency" :items="currencies" item-text="name" item-value="id" label="Moeda" outlined dense />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-if="clientCompanies.length"
              v-model="decision.client_company_id"
              :items="clientCompanies"
              item-text="name"
              item-value="id"
              label="Empresa devedora"
              outlined
              dense
              :rules="[(value) => !!value || 'Selecione a empresa devedora']"
              @change="onCompanyChange"
            />
            <v-text-field
              v-else
              v-model="decision.company_name"
              label="Empresa devedora"
              outlined
              dense
              hint="O caso não possui empresas devedoras cadastradas"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="decision.reference" label="Contrato / título / referência" outlined dense />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="decision.qgc_action" :items="qgcActions" label="Ação no QGC" outlined dense />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="decision.related_requests_action" :items="relatedActions" label="Tratamento dos pedidos relacionados" outlined dense />
          </v-col>
          <v-col cols="12">
            <div class="d-flex align-center justify-space-between mb-2">
              <p class="font-weight-bold mb-0">Motivo da análise</p>
              <v-btn color="primary" outlined small @click="reasonDialog = true">
                <v-icon left small>mdi-arrow-expand-all</v-icon>
                Expandir motivo
              </v-btn>
            </div>
            <!-- Um editor por vez: com o dialogo aberto, so o de tela cheia edita o texto -->
            <ui-document-editor v-if="!reasonDialog" v-model="decision.reason" orientation="landscape" :disabled="saving || concluding" :min-height="260" :max-height="560" no-fullscreen placeholder="Motivo da análise" />

            <v-dialog v-model="reasonDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
              <v-card class="reason-dialog">
                <v-toolbar dense flat color="primary" dark>
                  <v-toolbar-title>Motivo da análise</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-btn v-if="$allowed('edit')" text @click="reasonDialog = false"><v-icon left>mdi-check</v-icon> Concluir edição</v-btn>
                </v-toolbar>
                <v-card-text class="reason-dialog__body">
                  <div class="text-caption grey--text text--darken-1 mb-3">
                    O motivo é salvo com o rascunho e, ao concluir a análise, passa para o pedido de habilitação/divergência.
                  </div>
                  <ui-document-editor v-model="decision.reason" :disabled="saving || concluding" page orientation="landscape" no-fullscreen placeholder="Motivo da análise" />
                </v-card-text>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>

        <v-alert v-if="credit.possible_duplicate || conclusionConflict" type="error" text>
          <strong>Conclusão bloqueada — possível duplicidade.</strong>
          {{ conclusionConflict
            ? conclusionConflict.message
            : "Existe mais de um crédito candidato para a mesma combinação." }}
          <ul v-if="conclusionConflict && conclusionConflict.candidates.length" class="mt-2">
            <li v-for="candidate in conclusionConflict.candidates" :key="candidate.id">
              QGC #{{ candidate.id }} · {{ formatAmount(candidate.amount) }} ·
              {{ candidate.company_name || "empresa não informada" }}
            </li>
          </ul>
          <template v-if="canOverrideDuplicates">
            <v-checkbox
              v-model="duplicateOverride"
              label="Autorizar conclusão excepcional"
              hide-details
              class="mt-3"
            />
            <v-textarea
              v-if="duplicateOverride"
              v-model="duplicateJustification"
              label="Justificativa obrigatória da exceção"
              outlined
              rows="3"
              class="mt-3"
            />
          </template>
          <small v-else class="d-block mt-3">
            A exceção deve ser autorizada por um administrador com perfil Geral.
          </small>
        </v-alert>

        <div class="decision-actions">
          <v-btn v-if="$allowed('save')" outlined color="primary" :loading="saving" @click="saveDraft">Salvar rascunho</v-btn>
          <v-btn v-if="$allowed('credit.conclude')"
            color="primary"
            depressed
            :loading="concluding"
            :disabled="!canConclude"
            @click="concludeAnalysis"
          >
            Revisar e concluir análise
          </v-btn>
        </div>
      </ui-section>

      <ui-section title="7. Prévia do impacto no QGC" class="impact-section">
        <div class="impact-grid">
          <article>
            <span>SITUAÇÃO ATUAL · {{ versionLabel }}</span>
            <strong>{{ formatAmount(credit.values.qgc) }}</strong>
            <small>{{ credit.creditor_type_label }} · {{ credit.currency_code }}</small>
          </article>
          <v-icon size="28">mdi-arrow-right</v-icon>
          <article>
            <span>APÓS A ANÁLISE · VERSÃO DE TRABALHO</span>
            <strong>{{ formatAmount(decision.recognized_amount) }}</strong>
            <small>{{ selectedClassLabel }} · {{ selectedCurrencyLabel }}</small>
          </article>
        </div>
        <p class="impact-note">A publicação da próxima versão do QGC será uma ação separada e exigirá revisão completa.</p>
      </ui-section>
    </template>
    <debtor-document-request-dialog
      v-model="debtorRequestDialog"
      :client-id="clientId"
      :credits="credit ? [credit] : []"
      :default-target="documentRequestTarget"
      @sent="onDebtorRequestSent"
    />
  </div>
</template>

<script>
import axios from "axios";
import creditorTypes from "@/common/data/creditor-types.data";
import creditorClassUtil from "@/common/util/creditor-class.util";
import currencies from "@/common/data/currency-types.data";
import DebtorDocumentRequestDialog from "@/components/DebtorDocumentRequestDialog.vue";

export default {
  name: "CreditAnalysisDetails",

  components: { DebtorDocumentRequestDialog },

  data: () => ({
    loading: false,
    saving: false,
    concluding: false,
    conclusionConflict: null,
    duplicateOverride: false,
    duplicateJustification: "",
    reasonDialog: false,
    debtorRequestDialog: false,
    documentRequestTarget: "creditor",
    credit: null,
    clientCompanies: [],
    clientId: null,
    version: 3,
    creditKey: "",
    creditorTypes,
    currencies,
    decision: {
      outcome: null,
      recognized_amount: null,
      creditor_type: null,
      currency: null,
      client_company_id: null,
      company_name: "",
      reference: "",
      qgc_action: "update",
      related_requests_action: "joint",
      reason: "",
    },
    qgcActions: [
      { text: "Alterar crédito existente", value: "update" },
      { text: "Incluir novo crédito", value: "create" },
      { text: "Excluir crédito", value: "delete" },
      { text: "Manter inalterado", value: "keep" },
    ],
    relatedActions: [
      { text: "Encerrar como analisados em conjunto", value: "joint" },
      { text: "Manter em análise separadamente", value: "separate" },
      { text: "Arquivar por duplicidade", value: "duplicate" },
      { text: "Separar do agrupamento atual", value: "unlink" },
    ],
    outcomeOptions: [
      { text: "Aprovar", value: "approved" },
      { text: "Rejeitar", value: "rejected" },
      { text: "Aprovar parcialmente", value: "partial" },
    ],
  }),

  computed: {
    // Um registro por negocio do pedido de hab/div (kind user_event), na ordem de envio.
    eventSources() {
      return (this.credit && this.credit.sources || [])
        .filter((source) => source.kind === "user_event" && source.item_id)
        .filter((source, index, list) => list.findIndex((item) => Number(item.item_id) === Number(source.item_id)) === index);
    },

    breadcrumbs() {
      return [
        { text: "Homepage", disabled: false, to: "/home", exact: true },
        { text: "Central de Análise", disabled: false, to: { path: "/central-analise-creditos", query: { client_id: this.clientId } } },
        { text: "Análise completa", disabled: true },
      ];
    },

    valueDiverges() {
      if (!this.credit) return false;
      const values = [this.credit.values.creditor, this.credit.values.debtor, this.credit.values.aj]
        .filter((value) => value !== null && value !== undefined)
        .map(Number);
      return new Set(values).size > 1;
    },

    deadlineLabel() {
      if (!this.credit || this.credit.deadline_days === null) return "Sem prazo";
      if (this.credit.deadline_days < 0) return `${Math.abs(this.credit.deadline_days)} dias atrasado`;
      if (this.credit.deadline_days === 0) return "Vence hoje";
      return `${this.credit.deadline_days} dias`;
    },

    selectedClassLabel() {
      const found = this.creditorTypes.find((item) => Number(item.id) === Number(this.decision.creditor_type));
      return found ? found.name : "Classe não informada";
    },

    selectedCurrencyLabel() {
      const found = this.currencies.find((item) => Number(item.id) === Number(this.decision.currency));
      return found ? found.name : "Moeda não informada";
    },

    versionLabel() {
      return ({
        1: "Edital Art. 52 — lista inicial",
        2: "Edital Art. 7 — após Hab/Div",
        3: "QGC Consolidado — etapa atual",
      })[Number(this.version)] || `QGC v${this.version}`;
    },

    canOverrideDuplicates() {
      const user = (this.$root && this.$root.user) || {};
      return Number(user.profile) === 1;
    },

    canConclude() {
      if (!this.decision.outcome) return false;
      if (
        this.decision.outcome === "partial" &&
        (!Number.isFinite(Number(this.decision.recognized_amount)) || Number(this.decision.recognized_amount) < 0)
      ) return false;
      if (
        this.duplicateOverride &&
        (!this.canOverrideDuplicates || this.duplicateJustification.trim().length < 10)
      ) return false;
      if (this.clientCompanies.length && !this.decision.client_company_id) return false;
      return true;
    },
  },

  created() {
    this.clientId = Number(this.$route.query.client_id);
    this.loadCreditorClasses();
    this.version = Number(this.$route.query.version || 3);
    this.creditKey = String(this.$route.query.key || "");
    this.load();
  },

  methods: {
    async loadCreditorClasses() {
      // Inclui ocultas: a classe ja reconhecida precisa continuar selecionavel,
      // caso contrario salvar a analise perderia a classificacao.
      this.creditorTypes = await creditorClassUtil.listLabels(this.clientId);
    },

    creditorName(value) {
      return String(value || "Credor não identificado").toLocaleUpperCase("pt-BR");
    },

    qgcStageAmount(version) {
      const stage = this.credit && this.credit.qgc_stages && this.credit.qgc_stages[version];
      return stage ? stage.amount : null;
    },

    async load() {
      if (!this.clientId || !this.creditKey) return;
      this.loading = true;
      try {
        const [creditResponse, draftResponse] = await Promise.all([
          axios.get("/api/admin/credit-analysis/detail", {
            params: { client_id: this.clientId, version: this.version, credit_key: this.creditKey },
          }),
          axios.get("/api/admin/credit-analysis/draft", {
            params: { client_id: this.clientId, credit_key: this.creditKey },
          }),
        ]);
        this.credit = creditResponse.data.content.credit;
        this.clientCompanies =
          (creditResponse.data.content.context && creditResponse.data.content.context.client_companies) || [];
        const draft = draftResponse.data.content;
        if (draft && draft.decision) {
          this.decision = Object.assign({}, this.decision, draft.decision);
        } else {
          this.decision = Object.assign({}, this.decision, {
            recognized_amount: this.credit.values.aj ?? this.credit.values.creditor ?? this.credit.values.qgc,
            creditor_type: this.credit.creditor_type,
            currency: this.credit.currency,
            client_company_id: this.credit.client_company_id ||
              (this.clientCompanies.length === 1 ? this.clientCompanies[0].id : null),
            company_name: this.credit.company_name,
            reference: this.credit.reference || "",
            qgc_action: this.credit.qgc_count ? "update" : "create",
          });
        }
        this.onCompanyChange(this.decision.client_company_id);
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.loading = false;
      }
    },

    async saveDraft() {
      this.saving = true;
      try {
        await axios.put("/api/admin/credit-analysis/draft", {
          client_id: this.clientId,
          credit_key: this.creditKey,
          qgc_version: this.version,
          decision: this.decision,
        });
        this.$dialog.notify.success("Rascunho salvo com sucesso.", { position: "top-right", timeout: 3000 });
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.saving = false;
      }
    },

    async concludeAnalysis() {
      if (!this.canConclude) return;
      this.concluding = true;
      try {
        await axios.post("/api/admin/credit-analysis/conclude", {
          client_id: this.clientId,
          credit_key: this.creditKey,
          qgc_version: this.version,
          decision: this.decision,
          duplicate_override: this.duplicateOverride,
          duplicate_justification: this.duplicateOverride
            ? this.duplicateJustification.trim()
            : null,
        });
        this.$dialog.notify.success("Análise concluída e pedidos relacionados atualizados.", {
          position: "top-right",
          timeout: 4000,
        });
        this.goBack();
      } catch (error) {
        const response = error.response && error.response.data;
        const content = response && response.content;
        if (error.response && error.response.status === 409 && content) {
          this.conclusionConflict = {
            message: content.error && content.error.message
              ? content.error.message
              : "Existe possível duplicidade no QGC.",
            candidates: content.candidates || [],
          };
          this.$dialog.notify.error(this.conclusionConflict.message, {
            position: "top-right",
            timeout: 6000,
          });
        } else {
          this.notifyError(error);
        }
      } finally {
        this.concluding = false;
      }
    },

    onOutcomeChange(outcome) {
      if (outcome === "rejected") {
        this.decision.recognized_amount = 0;
        this.decision.qgc_action = this.credit.values.qgc === null ? "keep" : "delete";
        return;
      }

      if (outcome === "approved") {
        this.decision.recognized_amount =
          this.credit.values.aj ?? this.credit.values.debtor ?? this.credit.values.creditor ?? this.credit.values.qgc;
      }
    },

    onCompanyChange(companyId) {
      const company = this.clientCompanies.find(
        (item) => Number(item.id) === Number(companyId)
      );
      if (company) this.decision.company_name = company.name;
    },

    goBack() {
      this.$router.push({ path: "/central-analise-creditos", query: { client_id: this.clientId } });
    },

    sourcePath(source) {
      return source.kind === "official_letter" ? `/analise-oficio/${source.reference_id}` : `/divergencias/${source.reference_id}`;
    },

    openSourceNewTab(source) {
      const route = this.$router.resolve(this.sourcePath(source));
      window.open(route.href, "_blank", "noopener");
    },

    itemDocuments(source) {
      return (this.credit && this.credit.documents || []).filter(
        (document) => Number(document.source_item_id) === Number(source.item_id)
      );
    },

    itemStatusLabel(status) {
      return ({ 0: "Pendente", 1: "Aprovado", 2: "Rejeitado", 3: "Parcial", 4: "Documentos solicitados", 5: "Documentos enviados" })[Number(status)] || "Pendente";
    },

    itemStatusColor(status) {
      return ({ 1: "success", 2: "error", 3: "warning", 4: "orange", 5: "info" })[Number(status)] || "grey";
    },

    scrollToDecision() {
      const target = document.querySelector(".decision-anchor");
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    debtorRequestLabel(request) {
      const pending = (request.documents || []).filter((document) => !document.file_url).length;
      if (!pending) return "Documentos enviados";
      if (request.justification) return "Justificativa recebida";
      return `${pending} documento(s) pendente(s)`;
    },

    debtorRequestColor(request) {
      const pending = (request.documents || []).filter((document) => !document.file_url).length;
      if (!pending) return "success";
      return request.justification ? "info" : "warning";
    },

    onDebtorRequestSent() {
      this.load();
    },

    openDocumentRequest(target) {
      this.documentRequestTarget = target === "client" ? "client" : "creditor";
      this.debtorRequestDialog = true;
    },

    openSource(source) {
      this.$router.push(source.kind === "official_letter" ? `/analise-oficio/${source.reference_id}` : `/divergencias/${source.reference_id}`);
    },

    formatAmount(value) {
      if (value === null || value === undefined || value === "") return "—";
      const symbol = ({ 1: "R$", 2: "US$", 3: "€" })[this.decision.currency || (this.credit && this.credit.currency)] || "";
      return `${symbol} ${Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },

    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "Não informado";
    },

    sourceLabel(origin) {
      return ({ creditor: "Credor", debtor: "Recuperanda", aj: "AJ" })[origin] || origin;
    },

    sourceColor(origin) {
      return ({ creditor: "primary", debtor: "deep-purple", aj: "teal" })[origin] || "grey";
    },

    statusLabel(status) {
      return ({ not_started: "Não iniciado", triage: "Pendente de triagem", analysis: "Em análise", waiting_documents: "Aguardando documentos", conflict: "Com conflito", ready: "Pronto para concluir", concluded: "Concluído", approved: "Aprovado", rejected: "Rejeitado", partial: "Aprovado parcialmente", qgc_only: "Análise de ofício" })[status] || status;
    },

    statusColor(status) {
      return ({ conflict: "error", waiting_documents: "warning", ready: "success", concluded: "success", approved: "success", rejected: "error", partial: "teal", analysis: "primary", qgc_only: "info" })[status] || "blue-grey";
    },

    notifyError(error) {
      console.error(error);
      this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
    },
  },
};
</script>

<style scoped>
.business-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

.business-card {
  border-radius: 8px;
}

.business-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.reason-editor {
  min-height: 220px;
}

.reason-dialog__body {
  padding: 16px 24px;
}

.reason-editor--fullscreen {
  min-height: calc(100vh - 160px);
}

.identity-header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
}

.identity-header h3 {
  font-size: 19px;
  margin: 0;
}

.identity-header p {
  color: #667085;
  margin: 4px 0 0;
}

.metadata-grid {
  display: grid;
  gap: 1px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 20px;
}

.metadata-grid div {
  background: #f8fafc;
  padding: 12px;
}

.metadata-grid dt {
  color: #667085;
  font-size: 9px;
  text-transform: uppercase;
}

.metadata-grid dd {
  color: #344054;
  font-size: 12px;
  margin: 5px 0 0;
}

.comparison-table-wrapper {
  overflow-x: auto;
}

.comparison-table {
  border-collapse: collapse;
  min-width: 900px;
  width: 100%;
}

.comparison-table th,
.comparison-table td {
  border: 1px solid #e4e7ec;
  padding: 11px;
  text-align: left;
}

.comparison-table thead th {
  background: #f8fafc;
  color: #667085;
  font-size: 10px;
  text-transform: uppercase;
}

.comparison-table tbody th {
  color: #667085;
  font-size: 11px;
  width: 170px;
}

.comparison-table .divergent {
  background: #fff5f4;
  color: #b42318;
  font-weight: 500;
}

.decision-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.document-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.document-grid article {
  align-items: center;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  display: flex;
  gap: 10px;
  padding: 12px;
}

.document-grid article > div,
.history-timeline article > div {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.document-grid strong,
.history-timeline strong {
  color: #344054;
  font-size: 12px;
}

.document-grid small,
.history-timeline small {
  color: #667085;
  font-size: 10px;
}

.history-timeline {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.history-timeline article {
  align-items: flex-start;
  display: flex;
  gap: 11px;
  padding: 4px 0;
}

.timeline-dot {
  background: #98a2b3;
  border-radius: 50%;
  flex: none;
  height: 9px;
  margin-top: 4px;
  width: 9px;
}

.timeline-dot.creditor {
  background: var(--v-primary-base);
}

.timeline-dot.debtor {
  background: #6941c6;
}

.timeline-dot.aj {
  background: #0e6b5e;
}

.audit-note {
  margin: 18px 0 0;
}

.impact-grid {
  align-items: center;
  display: grid;
  gap: 18px;
  grid-template-columns: 1fr auto 1fr;
}

.impact-grid article {
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  padding: 18px;
}

.impact-grid span,
.impact-grid small,
.impact-note {
  color: #667085;
  font-size: 10px;
}

.impact-grid strong {
  font-size: 23px;
  margin: 9px 0 4px;
}

.impact-note {
  margin: 14px 0 0;
}

@media (max-width: 960px) {
  .metadata-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .document-grid {
    grid-template-columns: 1fr;
  }
}
</style>
